import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { RevisionReasonSchema, UploadMetadataSchema } from "./metadata.js";
const MAX_UPLOAD_BYTES = 2 * 1024 ** 3;
const UPLOAD_ALLOW_LIST = {
  pdf: ["application/pdf"],
  dwg: ["image/vnd.dwg", "application/acad", "application/dwg", "application/x-dwg", "image/x-dwg"],
  dxf: ["image/vnd.dxf", "application/dxf", "application/x-dxf", "image/x-dxf"],
  dwf: ["model/vnd.dwf", "drawing/x-dwf", "application/x-dwf"],
  ifc: ["application/x-step", "model/ifc", "application/ifc", "text/plain"],
  rvt: ["application/vnd.autodesk.revit"],
  skp: ["application/vnd.sketchup.skp", "application/x-koan"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xls: ["application/vnd.ms-excel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ppt: ["application/vnd.ms-powerpoint"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  odt: ["application/vnd.oasis.opendocument.text"],
  ods: ["application/vnd.oasis.opendocument.spreadsheet"],
  odp: ["application/vnd.oasis.opendocument.presentation"],
  rtf: ["application/rtf", "text/rtf"],
  txt: ["text/plain"],
  csv: ["text/csv", "text/plain", "application/vnd.ms-excel"],
  jpg: ["image/jpeg"],
  jpeg: ["image/jpeg"],
  png: ["image/png"],
  webp: ["image/webp"],
  heic: ["image/heic", "image/heif"],
  tif: ["image/tiff"],
  tiff: ["image/tiff"],
  bmp: ["image/bmp", "image/x-ms-bmp"],
  gif: ["image/gif"],
  svg: ["image/svg+xml"],
  zip: ["application/zip", "application/x-zip-compressed"],
  "7z": ["application/x-7z-compressed"],
  mp4: ["video/mp4"],
  mov: ["video/quicktime"],
  msg: ["application/vnd.ms-outlook"],
  eml: ["message/rfc822"],
  bcf: ["application/zip"]
};
const GENERIC_MIMES = /* @__PURE__ */ new Set(["", "application/octet-stream"]);
function extensionOf(filename) {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0 || dot === filename.length - 1) return null;
  return filename.slice(dot + 1).toLowerCase();
}
function allowedUpload(filename, declaredMime) {
  const ext = extensionOf(filename);
  if (!ext || !Object.hasOwn(UPLOAD_ALLOW_LIST, ext)) return null;
  const allowed = UPLOAD_ALLOW_LIST[ext];
  const mime = declaredMime.split(";")[0]?.trim().toLowerCase() ?? "";
  return GENERIC_MIMES.has(mime) || allowed.includes(mime) ? ext : null;
}
const UploadFilenameSchema = z.string().trim().min(1).max(255).regex(/^[^/\\\u0000-\u001f\u007f]+$/, "a file name without a path").refine((name) => name !== "." && name !== "..", "a file name other than . or ..");
const CreateUploadRequestSchema = z.strictObject({
  folderId: IdSchema,
  /** For a new version of an existing document (GED-BE-03). */
  documentId: IdSchema.optional(),
  filename: UploadFilenameSchema,
  size: z.int().min(1).max(Number.MAX_SAFE_INTEGER),
  mimeType: z.string().max(255),
  /** SHA-256 computed by the client (hex); checked by the worker when given. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  metadata: UploadMetadataSchema.optional(),
  revisionReason: RevisionReasonSchema.optional()
}).superRefine((body, ctx) => {
  if (body.documentId && !body.revisionReason)
    ctx.addIssue({
      code: "custom",
      path: ["revisionReason"],
      message: "a new version needs a reason"
    });
  if (body.documentId && body.metadata)
    ctx.addIssue({
      code: "custom",
      path: ["metadata"],
      message: "edit the document to change its metadata"
    });
  if (!body.documentId && body.revisionReason)
    ctx.addIssue({
      code: "custom",
      path: ["revisionReason"],
      message: "only a new version has a reason"
    });
});
const UploadPartUrlSchema = z.strictObject({
  partNumber: z.int().min(1).max(1e4),
  url: z.url()
});
const UploadSessionSchema = z.strictObject({
  uploadId: IdSchema,
  /** Bytes per part (the last part may be smaller). */
  partSize: z.int().min(1),
  parts: z.array(UploadPartUrlSchema),
  /** The part URLs stop working at this time; refresh them with …/parts. */
  urlsExpireAt: IsoDateTimeSchema,
  /** The upload must be completed before this time. */
  expiresAt: IsoDateTimeSchema
});
const UploadParamsSchema = z.strictObject({ spaceId: IdSchema, uploadId: IdSchema });
const RefreshUploadPartsRequestSchema = z.strictObject({
  partNumbers: z.array(z.int().min(1).max(1e4)).min(1).max(1e3)
});
const UploadPartsSchema = z.strictObject({
  parts: z.array(UploadPartUrlSchema),
  urlsExpireAt: IsoDateTimeSchema
});
const CompleteUploadRequestSchema = z.strictObject({
  parts: z.array(
    z.strictObject({
      partNumber: z.int().min(1).max(1e4),
      etag: z.string().min(1).max(200)
    })
  ).min(1).max(1e4)
});
const DOCUMENT_VERSION_STATUSES = [
  "uploading",
  "scanning",
  "available",
  "rejected"
];
const DocumentVersionStatusSchema = z.enum(DOCUMENT_VERSION_STATUSES);
const UPLOAD_REJECTION_REASONS = [
  "infected",
  "scan_limits",
  "type_mismatch",
  "html_content",
  "size_mismatch",
  "sha256_mismatch"
];
const UploadRejectionReasonSchema = z.enum(UPLOAD_REJECTION_REASONS);
const CompletedUploadSchema = z.strictObject({
  documentId: IdSchema,
  versionId: IdSchema,
  status: DocumentVersionStatusSchema
});
const UploadStatusSchema = z.strictObject({
  uploadId: IdSchema,
  status: z.enum(["open", "completed", "aborted"]),
  documentId: IdSchema.nullable(),
  versionId: IdSchema.nullable(),
  versionStatus: DocumentVersionStatusSchema.nullable(),
  rejectionReason: UploadRejectionReasonSchema.nullable()
});
export {
  CompleteUploadRequestSchema,
  CompletedUploadSchema,
  CreateUploadRequestSchema,
  DOCUMENT_VERSION_STATUSES,
  DocumentVersionStatusSchema,
  MAX_UPLOAD_BYTES,
  RefreshUploadPartsRequestSchema,
  UPLOAD_ALLOW_LIST,
  UPLOAD_REJECTION_REASONS,
  UploadFilenameSchema,
  UploadParamsSchema,
  UploadPartUrlSchema,
  UploadPartsSchema,
  UploadRejectionReasonSchema,
  UploadSessionSchema,
  UploadStatusSchema,
  allowedUpload,
  extensionOf
};
//# sourceMappingURL=uploads.js.map